// Import Types
import type { CJStepsValuesShape } from './types';
import type { StepperHeadlineData } from 'components/common/Stepper/StepperPageHeadline/StepperPageHeadline';
// Import Enums
import {
    AwardBudgetSetupTypesEnum,
    BudgetLimitTypeEnum,
    CJStepsEnum,
    //  AwardBudgetSetupTypesEnum,
    //   BudgetLimitTypeEnum
} from 'enums';
import type { InformationStepFormValues } from './steps/InformationStep/types';
import type { CJRequestBodyModel, CJScheduleRequestModel } from 'types';
// Import Helpers
import moment from 'moment-timezone';
import { isNil, isEmpty } from 'lodash';
import { buildCustomFieldsRequestModel } from 'services/Helpers';
// Import Constants
import { DateToISOStringFormat } from 'constants/common';

export const buildDateTime = (date: Nullable<string>, time: Nullable<string>): string => {
    const parsed = moment(date);
    const m = moment.utc({ year: parsed.year(), month: parsed.month(), date: parsed.date() });
    if (time) {
        const [hours, minutes] = time.split(':');
        m.set({ hour: +hours, minute: +minutes });
    }
    return m.format(DateToISOStringFormat);
};

const buildSchedule = (info: InformationStepFormValues): CJScheduleRequestModel | null => {
    const { isActiveStartDate, autoStartDate, autoStartTime, isActiveEndDate, autoEndDate, autoEndTime, timeZone } =
        info;

    return {
        StartDate: isActiveStartDate && autoStartDate ? buildDateTime(autoStartDate, autoStartTime) : null,
        EndDate: isActiveEndDate && autoEndDate ? buildDateTime(autoEndDate, autoEndTime) : null,
        CronSchedule: null,
        TimeZone: timeZone ?? undefined,
        isActiveStartDate,
        isActiveEndDate,
        startTime: isActiveStartDate && autoStartTime ? autoStartTime : undefined,
        endTime: isActiveEndDate && autoEndTime ? autoEndTime : undefined,
    };
};

const fillJourneyStepRequestModelAPI = (formData: CJStepsValuesShape, requestModel: CJRequestBodyModel) => {
    Object.assign(requestModel, formData[CJStepsEnum.Journey].requestBody);
};

const fillInfoStepRequestModelAPI = (formData: CJStepsValuesShape, requestModel: AnyObject, isPredefined?: boolean) => {
    const info = formData[CJStepsEnum.Info] as InformationStepFormValues;
    const customFields = buildCustomFieldsRequestModel(info.customField);

    Object.assign(requestModel, {
        IsPublic: info.isPublic,
        IsNonMarketing: info.isNonMarketing,
        Schedule: buildSchedule(info),
        CustomFields: customFields,
        ExclusionRules: info.exclusionRules,
        EfficiencyMetric:
            !isNil(info.trackingPeriod) && `${info.trackingPeriod}` !== '' && !isNil(info.metricId)
                ? { AnalyticsPeriod: Number(info.trackingPeriod), MetricId: info.metricId }
                : null,
        ...(isPredefined ? { PredefinedCategories: info.categories, PredefinedRegions: info.regions } : {}),
    });
};
const mapBlocksToApiModel = (blocks) => {
    return Object.keys(blocks).reduce((acc, blockId) => {
        acc[blockId] = blocks[blockId].rate;
        return acc;
    }, {});
};

const fillBudgetStepRequestModelAPI = (
    formData: CJStepsValuesShape,
    requestModel: AnyObject,
    isPredefined?: boolean,
) => {
    // Predefined templates have no budget (the step is hidden); send an empty list so any existing
    // or cloned budget data is cleared on the backend.
    if (isPredefined) {
        Object.assign(requestModel, { Budgets: [] });
        return;
    }

    const budgetGroups = formData[CJStepsEnum.BudgetLimit]?.budget;
    if (isEmpty(budgetGroups)) return;
    const tmpRequestModel = budgetGroups.map((budgetGroup) => {
        let tmpResult = {
            Name: budgetGroup.name,
            BudgetId: budgetGroup.budgetId,
            AdditionalLimits: {
                Blocks: mapBlocksToApiModel(budgetGroup.bonusBlocks),
            },
        } as AnyObject;

        if (budgetGroup.budgetSetupActiveTabKey === AwardBudgetSetupTypesEnum.CountLimit) {
            const period = budgetGroup.budgetPeriod?.[AwardBudgetSetupTypesEnum.CountLimit];
            const behavior = budgetGroup.limitBehavior?.[AwardBudgetSetupTypesEnum.CountLimit];
            const limitValue = budgetGroup.countLimitCount;
            if (!isNil(period) && !isNil(behavior) && !isNil(limitValue)) {
                tmpResult = {
                    ...tmpResult,
                    LimitType: BudgetLimitTypeEnum.Count,
                    Behavior: behavior,
                    LimitValue: limitValue,
                    Period: period,
                };
            }
        } else if (budgetGroup.budgetSetupActiveTabKey === AwardBudgetSetupTypesEnum.AmountLimit) {
            const period = budgetGroup.budgetPeriod?.[AwardBudgetSetupTypesEnum.AmountLimit];
            const behavior = budgetGroup.limitBehavior?.[AwardBudgetSetupTypesEnum.AmountLimit];
            if (
                !isNil(period) &&
                !isNil(behavior) &&
                !isNil(budgetGroup.amountLimitAmount) &&
                !isNil(budgetGroup.amountLimitCurrency)
            ) {
                tmpResult = {
                    ...tmpResult,
                    LimitType: BudgetLimitTypeEnum.Amount,
                    Behavior: behavior,
                    LimitValue: budgetGroup.amountLimitAmount,
                    Period: period,
                    CurrencyId: budgetGroup.amountLimitCurrency,
                };
            }
        }

        return tmpResult;
    });

    Object.assign(requestModel, { Budgets: tmpRequestModel });
};

export const parseCJValuesToCECRequestModelAPI = (
    formData: CJStepsValuesShape,
    status: Nilable<number>,
    headlineData: StepperHeadlineData,
    isPredefined?: boolean,
): CJRequestBodyModel => {
    // TODO: Create RequestModel interface based on API and fill it with formData from all steps
    const requestModel: CJRequestBodyModel = {
        Status: status,
        Name: headlineData.name,
        Description: headlineData.description,
    } as unknown as CJRequestBodyModel;

    fillJourneyStepRequestModelAPI(formData, requestModel);
    fillInfoStepRequestModelAPI(formData, requestModel, isPredefined);
    fillBudgetStepRequestModelAPI(formData, requestModel, isPredefined);

    return requestModel;
};
